import { createAsyncThunk } from "@reduxjs/toolkit";
import { get, post, put, del } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { DEFAULT_LIST_PAGE_SIZE } from "../../Utility/Pagination/listPaginationConstants";

function appendRepeatedTeamIdsToEndpoint(endpoint, teamIds) {
  if (!Array.isArray(teamIds) || teamIds.length === 0) return endpoint;
  const teamQuery = teamIds.map((id) => `teamId=${encodeURIComponent(id)}`).join("&");
  return `${endpoint}?${teamQuery}`;
}

/**
 * Fetch users data with optional pagination and filters
 * GET /api/v1/users?page=1&limit=10&search=…&role=DEVELOPER&is_active=0&teamId=…&teamId=…
 * Returns raw API response: { success, data: { data: [users], meta } }
 */
export const fetchUsersData = createAsyncThunk(
  "users/fetchData",
  async (params = {}, { getState, rejectWithValue }) => {
    const getAll = params.getAll === true;
    if (getAll && !params.force) {
      const { allListLoaded, allListData } = getState().users;
      if (allListLoaded && allListData != null) {
        return allListData;
      }
    }
    try {
      const endpoint = getAll ? API_ENDPOINTS.USERS.GET_ALL_LIST : API_ENDPOINTS.USERS.GET_ALL;
      const { query, teamIds } = getAll
        ? { query: {}, teamIds: null }
        : (() => {
            const q = { page: params.page ?? 1, limit: params.limit ?? DEFAULT_LIST_PAGE_SIZE };
            if (params.search != null && String(params.search).trim() !== "")
              q.search = String(params.search).trim();
            if (params.role != null && String(params.role).trim() !== "")
              q.role = String(params.role).trim();
            if (params.is_active != null && String(params.is_active).trim() !== "") {
              const raw = String(params.is_active).trim().toLowerCase();
              if (raw === "active" || raw === "1" || raw === "true") q.is_active = 1;
              else if (raw === "inactive" || raw === "0" || raw === "false") q.is_active = 0;
            }
            const ids = (() => {
              const raw = params.teamId ?? params.teamIds;
              if (raw == null) return null;
              const list = Array.isArray(raw) ? raw : [raw];
              const normalized = list.map((id) => String(id).trim()).filter(Boolean);
              return normalized.length > 0 ? normalized : null;
            })();
            return { query: q, teamIds: ids };
          })();
      const requestUrl = appendRepeatedTeamIdsToEndpoint(endpoint, teamIds);
      const response = await get(requestUrl, query);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch users data"));
    }
  }
);

/**
 * Fetch user by ID
 */
export const fetchUserById = createAsyncThunk(
  "users/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.USERS.GET_BY_ID(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch user data"));
    }
  }
);

/**
 * Create a new user (system-generated password; verification email sent)
 * Body: { email, name }
 */
export const createUser = createAsyncThunk(
  "users/create",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await post(API_ENDPOINTS.USERS.CREATE, userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to create user"));
    }
  }
);

/**
 * Verify email with token from verification link
 * Body: { token }
 */
export const verifyEmail = createAsyncThunk(
  "users/verifyEmail",
  async (token, { rejectWithValue }) => {
    try {
      const response = await post(API_ENDPOINTS.USERS.VERIFY_EMAIL, { token });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Verification failed"));
    }
  }
);

/**
 * Update an existing user
 */
export const updateUser = createAsyncThunk(
  "users/update",
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const endpoint =
        typeof API_ENDPOINTS.USERS.UPDATE === "function"
          ? API_ENDPOINTS.USERS.UPDATE(id)
          : `${API_ENDPOINTS.USERS.UPDATE}/${id}`;

      const response = await put(endpoint, userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to update user"));
    }
  }
);

/**
 * List roles for users – GET /users/roles (e.g. for Add/Edit User role dropdown)
 */
export const fetchUserRoles = createAsyncThunk(
  "users/fetchRoles",
  async (params = {}, { getState, rejectWithValue }) => {
    const force = params?.force === true;
    if (!force) {
      const { userRolesLoaded, userRolesData } = getState().users;
      if (userRolesLoaded && userRolesData != null) {
        return userRolesData;
      }
    }
    try {
      const response = await get(API_ENDPOINTS.USERS.GET_ROLES);
      const raw = response.data;
      const data = raw?.data ?? raw;
      const list = Array.isArray(data) ? data : (data?.list ?? data?.data ?? []);
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch roles"));
    }
  }
);

/**
 * Delete a user
 */
export const deleteUser = createAsyncThunk("users/delete", async (id, { rejectWithValue }) => {
  try {
    await del(API_ENDPOINTS.USERS.DELETE(id), {}, { skipToast: true });
    return id; // Return the deleted user ID
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error, "Failed to delete user"));
  }
});

/** Admin: send password reset email to a user — same API as public forgot-password flow. */
export const sendUserPasswordResetEmail = createAsyncThunk(
  "users/sendPasswordResetEmail",
  async (email, { rejectWithValue }) => {
    const trimmed = String(email ?? "").trim();
    if (!trimmed) {
      return rejectWithValue("User email is missing.");
    }
    try {
      await post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email: trimmed }, {}, "json", {
        skipToast: true,
      });
      return trimmed;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to send password reset email"));
    }
  }
);

/** Admin: resend email verification link — POST /users/:id/send-verification-email */
export const sendUserVerificationEmail = createAsyncThunk(
  "users/sendVerificationEmail",
  async (userId, { rejectWithValue }) => {
    const id = String(userId ?? "").trim();
    if (!id) {
      return rejectWithValue("User id is missing.");
    }
    try {
      await post(API_ENDPOINTS.USERS.SEND_VERIFICATION_EMAIL(id), {}, {}, "json", {
        skipToast: true,
      });
      return id;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to send verification email"));
    }
  }
);
